import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { GlassCard } from "@/components/GlassCard";
import { StatusBadge } from "@/components/StatusBadge";
import { FlowchartCanvas } from "@/components/FlowchartCanvas";
import { ESignatureModal } from "@/components/ESignatureModal";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  Check,
  Clock,
  History,
  MessageSquare,
  PenSquare,
  Play,
  ShieldCheck,
  Undo2,
  XCircle,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Textarea } from "@/components/ui/textarea";

export default function SOPViewer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [sop, setSop] = useState(null);
  const [version, setVersion] = useState(null);
  const [actions, setActions] = useState([]);
  const [comments, setComments] = useState([]);
  const [users, setUsers] = useState([]);
  const [acks, setAcks] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState("");
  const [modal, setModal] = useState(null); // 'approve' | 'reject' | 'request_changes' | 'ack'

  const load = async () => {
    try {
      setLoading(true);
      const [s, a, c, u, ak] = await Promise.all([
        api.get(`/sops/${id}`),
        api.get(`/sops/${id}/actions`).catch(() => ({ data: [] })),
        api.get(`/sops/${id}/comments`).catch(() => ({ data: [] })),
        api.get("/users").catch(() => ({ data: [] })),
        api.get(`/sops/${id}/acknowledgements`).catch(() => ({ data: [] })),
      ]);
      setSop(s.data.sop);
      setVersion(s.data.current_version);
      setActions(a.data);
      setComments(c.data);
      setUsers(u.data);
      setAcks(ak.data);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Failed to load SOP");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const userMap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);

  const isOwnerOrCreator = sop && user && (sop.owner_id === user.id || sop.created_by === user.id || user.role === "admin");
  const isReviewer = user && sop?.status === "in_review" && (user.role === "owner" || user.role === "admin");
  const alreadyAcked = acks.some((a) => a.user_id === user?.id && a.sop_version_id === sop?.current_version_id);

  const submitAction = (action) => async (name, comment) => {
    await api.post(`/sops/${id}/actions`, {
      action,
      comment,
      signature_name: name,
    });
    await load();
  };

  const submitAck = async (name) => {
    await api.post(`/sops/${id}/acknowledge`, { signature_name: name });
    await load();
  };

  const addComment = async () => {
    if (!newComment.trim()) return;
    await api.post(`/sops/${id}/comments`, { text: newComment.trim() });
    setNewComment("");
    load();
  };

  if (loading) {
    return (
      <div className="pt-10 flex justify-center">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#2563EB] to-[#4F46E5] animate-pulse-glow" />
      </div>
    );
  }

  if (err || !sop) {
    return <GlassCard className="p-10 text-center text-rose-300">{err || "Not found"}</GlassCard>;
  }

  const owner = userMap[sop.owner_id];

  return (
    <div className="space-y-6" data-testid="sop-viewer-page">
      <button
        onClick={() => navigate(-1)}
        className="text-xs text-[#9AA4B2] hover:text-white flex items-center gap-1"
        data-testid="viewer-back"
      >
        <ArrowLeft size={12} /> Back
      </button>

      <header className="flex flex-col md:flex-row md:items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <StatusBadge status={sop.status} />
            <span className="text-[11px] uppercase tracking-widest text-[#5B6472]">
              v{sop.current_version_number}
            </span>
            {sop.expires_at && (
              <span className="text-[11px] uppercase tracking-widest text-[#5B6472]">
                Expires {new Date(sop.expires_at).toLocaleDateString()}
              </span>
            )}
          </div>
          <h1 className="mt-2 font-display text-3xl md:text-4xl font-semibold text-white leading-tight">
            {sop.title}
          </h1>
          <p className="mt-1 text-[#9AA4B2] max-w-2xl">{sop.description}</p>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-[#9AA4B2]">
            <span className="px-2 py-1 rounded-full bg-white/[0.04] border border-white/10">
              Owner: {owner?.name || "—"}
            </span>
            <span className="px-2 py-1 rounded-full bg-white/[0.04] border border-white/10">
              Dept: {sop.department || "—"}
            </span>
            {sop.tags?.map((t) => (
              <span key={t} className="px-2 py-1 rounded-full bg-white/[0.04] border border-white/10">
                #{t}
              </span>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {isOwnerOrCreator && sop.status === "draft" && (
            <Button
              onClick={() => navigate(`/sops/${sop.id}/edit`)}
              className="btn-ghost-glass rounded-full px-5"
              data-testid="viewer-edit-btn"
            >
              <PenSquare size={14} className="mr-1.5" /> Edit draft
            </Button>
          )}
          {isOwnerOrCreator && sop.status === "approved" && (
            <Button
              onClick={async () => {
                await api.post(`/sops/${sop.id}/versions`);
                navigate(`/sops/${sop.id}/edit`);
              }}
              className="btn-ghost-glass rounded-full px-5"
              data-testid="viewer-new-rev-btn"
            >
              <PenSquare size={14} className="mr-1.5" /> Start new revision
            </Button>
          )}
          <Button
            onClick={() => navigate(`/sops/${sop.id}/versions`)}
            variant="ghost"
            className="btn-ghost-glass rounded-full px-5"
            data-testid="viewer-versions-btn"
          >
            <History size={14} className="mr-1.5" /> Version history
          </Button>
          {(sop.status === "approved" || (version?.steps || []).length > 0) && (
            <Button
              onClick={() => navigate(`/sops/${sop.id}/execute`)}
              className="btn-primary-glow rounded-full px-5"
              data-testid="viewer-execute-btn"
            >
              <Play size={14} className="mr-1.5" /> Execute
            </Button>
          )}
          {sop.status === "approved" && !alreadyAcked && (
            <Button
              onClick={() => setModal("ack")}
              className="btn-primary-glow rounded-full px-5"
              data-testid="viewer-ack-btn"
            >
              <ShieldCheck size={14} className="mr-1.5" /> Acknowledge
            </Button>
          )}
          {sop.status === "approved" && alreadyAcked && (
            <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-400/30" data-testid="viewer-acked-chip">
              <Check size={14} /> Acknowledged
            </span>
          )}
          {isReviewer && sop.status === "in_review" && (
            <>
              <Button
                onClick={() => setModal("request_changes")}
                className="btn-ghost-glass rounded-full px-5"
                data-testid="viewer-request-changes-btn"
              >
                <Undo2 size={14} className="mr-1.5" /> Request changes
              </Button>
              <Button
                onClick={() => setModal("reject")}
                variant="ghost"
                className="btn-ghost-glass rounded-full px-5 text-rose-300"
                data-testid="viewer-reject-btn"
              >
                <XCircle size={14} className="mr-1.5" /> Reject
              </Button>
              <Button
                onClick={() => setModal("approve")}
                className="btn-primary-glow rounded-full px-5"
                data-testid="viewer-approve-btn"
              >
                <Check size={14} className="mr-1.5" /> Approve & sign
              </Button>
            </>
          )}
        </div>
      </header>

      {sop.status === "superseded" && (
        <GlassCard className="p-4 border border-slate-500/30 bg-slate-500/5 text-slate-200 text-sm" data-testid="viewer-superseded-banner">
          This version has been superseded. Always confirm you&apos;re reading the latest published version.
        </GlassCard>
      )}

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Content + flowchart */}
        <div className="lg:col-span-2 space-y-4">
          <GlassCard className="p-6">
            <h2 className="font-display text-xl text-white mb-2">Procedure</h2>
            <div
              className="prose-sop"
              data-testid="viewer-content"
              dangerouslySetInnerHTML={{
                __html: version?.content || "<p class='text-[#5B6472]'>No content yet.</p>",
              }}
            />
          </GlassCard>

          {(version?.flowchart?.nodes?.length || 0) > 0 && (
            <GlassCard className="p-6">
              <h2 className="font-display text-xl text-white mb-3">Flow diagram</h2>
              <FlowchartCanvas initial={version.flowchart} readOnly />
            </GlassCard>
          )}

          {/* Comments */}
          <GlassCard className="p-6" data-testid="viewer-comments">
            <h2 className="font-display text-xl text-white mb-3 flex items-center gap-2">
              <MessageSquare size={18} /> Feedback
            </h2>
            <div className="space-y-3">
              {comments.length === 0 ? (
                <div className="text-sm text-[#5B6472]">No comments yet.</div>
              ) : (
                comments.map((c) => (
                  <div key={c.id} className="flex gap-3">
                    <div className="w-8 h-8 shrink-0 rounded-full bg-gradient-to-br from-indigo-500/60 to-cyan-500/60 flex items-center justify-center text-xs text-white font-semibold">
                      {(userMap[c.user_id]?.name || "?").slice(0, 1)}
                    </div>
                    <div className="flex-1 glass rounded-2xl p-3">
                      <div className="text-xs text-[#9AA4B2]">
                        <span className="text-white">{userMap[c.user_id]?.name || "User"}</span> ·{" "}
                        {new Date(c.created_at).toLocaleString()}
                      </div>
                      <div className="mt-1 text-sm text-white whitespace-pre-wrap">{c.text}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="mt-4 flex gap-2">
              <Textarea
                data-testid="viewer-comment-input"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Leave feedback…"
                className="bg-white/[0.04] border-white/10 rounded-2xl text-white placeholder:text-[#5B6472]"
                rows={2}
              />
              <Button onClick={addComment} className="btn-primary-glow rounded-full self-end" data-testid="viewer-comment-submit">
                Send
              </Button>
            </div>
          </GlassCard>
        </div>

        {/* Right rail: activity + acks */}
        <div className="space-y-4">
          <GlassCard className="p-5">
            <h3 className="font-display text-white mb-3 flex items-center gap-2">
              <Clock size={16} /> Activity
            </h3>
            <ol className="relative border-l border-white/10 ml-2">
              {actions.length === 0 && <li className="text-sm text-[#5B6472] pl-4">No approvals or signatures yet.</li>}
              {actions.map((a) => (
                <li key={a.id} className="mb-4 ml-4">
                  <div className={`absolute -left-1.5 mt-1 w-3 h-3 rounded-full ${
                    a.action === "approve" ? "bg-emerald-400 shadow-[0_0_10px_rgba(16,185,129,0.7)]"
                    : a.action === "reject" ? "bg-rose-400 shadow-[0_0_10px_rgba(244,63,94,0.7)]"
                    : a.action === "request_changes" ? "bg-amber-400 shadow-[0_0_10px_rgba(245,158,11,0.7)]"
                    : "bg-indigo-400"
                  }`} />
                  <div className="text-sm text-white capitalize">{a.action.replace("_", " ")}</div>
                  <div className="text-[11px] text-[#9AA4B2]">
                    {userMap[a.user_id]?.name || "User"} · {new Date(a.timestamp).toLocaleString()}
                  </div>
                  {a.signature_name && (
                    <div className="text-[10px] text-emerald-400/80 mt-0.5">
                      Signed: {a.signature_name} · {a.signature_ip}
                    </div>
                  )}
                  {a.comment && <div className="text-xs text-[#9AA4B2] mt-1">&ldquo;{a.comment}&rdquo;</div>}
                </li>
              ))}
            </ol>
          </GlassCard>

          {sop.status === "approved" && (
            <GlassCard className="p-5" data-testid="viewer-ack-panel">
              <h3 className="font-display text-white mb-2">Acknowledgements</h3>
              <div className="text-3xl font-display text-emerald-300">{acks.length}</div>
              <div className="text-xs text-[#9AA4B2]">of {users.filter((u) => u.status !== "inactive").length} users</div>
              <div className="mt-3 space-y-1 max-h-40 overflow-y-auto">
                {acks.map((a) => (
                  <div key={a.id} className="text-xs text-[#9AA4B2] flex justify-between">
                    <span>{userMap[a.user_id]?.name || "User"}</span>
                    <span>{new Date(a.read_at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            </GlassCard>
          )}
        </div>
      </div>

      <ESignatureModal
        open={modal === "approve"}
        onOpenChange={(o) => !o && setModal(null)}
        title="Approve & sign this SOP"
        action="approve"
        onConfirm={submitAction("approve")}
        defaultName={user?.name || ""}
      />
      <ESignatureModal
        open={modal === "request_changes"}
        onOpenChange={(o) => !o && setModal(null)}
        title="Request changes"
        action="request_changes"
        requireComment
        onConfirm={submitAction("request_changes")}
        defaultName={user?.name || ""}
      />
      <ESignatureModal
        open={modal === "reject"}
        onOpenChange={(o) => !o && setModal(null)}
        title="Reject this submission"
        action="reject"
        requireComment
        onConfirm={submitAction("reject")}
        defaultName={user?.name || ""}
      />
      <ESignatureModal
        open={modal === "ack"}
        onOpenChange={(o) => !o && setModal(null)}
        title="Acknowledge & sign"
        action="acknowledge"
        onConfirm={async (name) => submitAck(name)}
        defaultName={user?.name || ""}
      />
    </div>
  );
}
